import AurDataGridCellWrapper from '../../common/aur-data-grid/AurDataGridCellWrapper';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import AurIconButton from '../../common/aur-button/AurIconButton';
import {ReactComponent as RightChev} from '../../../assests/svg/chev_right.svg';
import {GridRenderHeaderFilterProps} from '@mui/x-data-grid-pro';
import FilterTextField from '../../common/aur-data-grid-pro/FilterTextField';
import {AurDataGridColDef} from '../../../types/types';
import {t} from 'i18next';
import AurCountryFlag from '../../common/aur-country-flag/AurCountryFlag';
import {Box} from '@mui/material';
import {ONLY_DATE_SHOW_FORMAT} from '../../../constants/constants';

const getHostCountryCellContent = (
  countryName: string,
  countryCode: string
) => {
  if (!countryName)
    return <AurDataGridCellWrapper cellData="--" tooltipDisabled={true} />;
  const cellContent = (
    <span>
      <AurCountryFlag
        countryCode={countryCode}
        altText={countryCode.toUpperCase()}
      />
      <Box component="span" paddingLeft="5px">
        {countryName}
      </Box>
    </span>
  );
  return (
    <AurDataGridCellWrapper
      cellData={countryName}
      tooltipText={countryName}
      customCellContent={cellContent}
    />
  );
};

const renderEmptyHeader = (label: string) => (
  <span className="sr-only">{label}</span>
);

export const getProjectTableColumnDef = (
  handleChevronClick: (rowIndex: number) => void
): AurDataGridColDef[] => [
  {
    field: 'projectName',
    headerName: t('clientDetailsPage.projectTable.header.projectName'),
    flex: 1.3,
    sortable: true,
    renderCell: (params) => {
      let cellContent = <AurDataGridCellWrapper cellData={params.value} />;
      const isHighlyConfidential = params.row.highlyConfidential ?? false;
      if (isHighlyConfidential) {
        cellContent = (
          <AurDataGridCellWrapper
            cellData={params.value}
            customCellContent={
              <>
                <div className="aur-datagrid-cell-ellipsis">{params.value}</div>
                <div className="aur-datagrid-cell-highly-confidential">
                  {t('highlyConfidential.label')}
                </div>
              </>
            }
          />
        );
      }
      return cellContent;
    },
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          gridRenderHeaderFilterProps={params}
          label={t('common.filters.contains')}
        />
      );
    },
  },
  {
    field: 'countryName',
    headerName: t('clientDetailsPage.projectTable.header.hostCountry'),
    flex: 0.9,
    sortable: false,
    renderCell: (param) =>
      getHostCountryCellContent(param.value, param.row.domainCountryCode),
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          gridRenderHeaderFilterProps={params}
          label={t('common.filters.contains')}
        />
      );
    },
  },
  {
    field: 'dataCenterName',
    headerName: t('clientDetailsPage.projectTable.header.dataCenterInstance'),
    flex: 1.2,
    sortable: false,
    renderCell: (param) => (
      <AurDataGridCellWrapper
        cellData={param.value || '--'}
        tooltipText={param.value}
        tooltipDisabled={!param.value}
      />
    ),
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          gridRenderHeaderFilterProps={params}
          label={t('common.filters.contains')}
        />
      );
    },
  },
  {
    field: 'createdOn',
    headerName: t('clientDetailsPage.projectTable.header.createdOn'),
    flex: 0.9,
    sortable: false,
    renderCell: (param) => (
      <AurDataGridCellWrapper
        cellData={getUTCDateTimeForDisplay(param.value, ONLY_DATE_SHOW_FORMAT)}
        tooltipText={t('common.yearMonthDate')}
      />
    ),
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          gridRenderHeaderFilterProps={params}
          label={t('common.filters.contains')}
        />
      );
    },
  },
  {
    field: 'projectCode',
    headerName: t('clientDetailsPage.projectTable.header.engagementCode'),
    flex: 0.9,
    sortable: false,
    renderCell: (params) => <AurDataGridCellWrapper cellData={params.value} />,
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          gridRenderHeaderFilterProps={params}
          label={t('common.filters.contains')}
        />
      );
    },
  },
  {
    field: 'branchName',
    headerName: t('clientDetailsPage.projectTable.header.branch'),
    flex: 0.8,
    sortable: false,
    renderCell: (params) => <AurDataGridCellWrapper cellData={params.value} />,
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          gridRenderHeaderFilterProps={params}
          label={t('common.filters.contains')}
        />
      );
    },
  },
  {
    field: 'departmentName',
    headerName: t('clientDetailsPage.projectTable.header.department'),
    flex: 0.9,
    sortable: false,
    renderCell: (params) => <AurDataGridCellWrapper cellData={params.value} />,
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          gridRenderHeaderFilterProps={params}
          label={t('common.filters.contains')}
        />
      );
    },
  },
  {
    field: 'workspaces',
    headerName: t('clientDetailsPage.projectTable.header.workspaces'),
    flex: 1,
    sortable: false,
    renderCell: (params) => <AurDataGridCellWrapper cellData={params.value} />,
    renderHeaderFilter: (params: GridRenderHeaderFilterProps) => {
      return (
        <FilterTextField
          gridRenderHeaderFilterProps={params}
          label={t('common.filters.contains')}
        />
      );
    },
  },
  {
    field: 'actions',
    flex: 0.5,
    sortable: false,
    filterable: false,
    isBorderNotRequired: true,
    renderCell: (params) => (
      <AurIconButton
        dataTestId={'action-button'}
        className="grid-action-button"
        ariaLabel={t(
          'clientDetailsPage.projectTable.action.viewProjectDetails'
        )}
        icon={<RightChev aria-hidden="true" />}
        onClick={() => {
          handleChevronClick(
            params.api.getRowIndexRelativeToVisibleRows(params.id)
          );
        }}
      />
    ),
    renderHeader: () =>
      renderEmptyHeader(
        `${t('clientDetailsPage.projectTable.action.viewProjectDetails')}`
      ),
    renderHeaderFilter: () =>
      renderEmptyHeader(
        `${t('clientDetailsPage.projectTable.action.viewProjectDetails')}`
      ),
  },
];
